/**
 * Cancellation refund-bands API wrapper (v2 — Project Independence, DNKADM-0057 U2).
 *
 * The Policies screen's refund ladder: how much of a booking's credits are refunded
 * based on how far ahead it is cancelled, optionally per credit bucket (handoff §5.12).
 * A `v2_cancellation_band` is keyed by a `scope` over an optional credit BUCKET:
 *
 *  - `scope` (`ALL|BUCKET`) — `ALL` = all buckets, `BUCKET` = one bucket;
 *  - `creditBucketId` (a 0052 first-class bucket id, or `null` = all buckets);
 *  - `minHoursBeforeStart` (INT — qualify when `(start - now) >= this`);
 *  - `refundPercent` (`DECIMAL(5,2)`, `0.00–100.00`).
 *
 * Unique on `(tenant, scope, bucket, minHours)` — the create/update `409`/`400`
 * surfaces. A cancellation is evaluated by the **largest qualifying**
 * `minHoursBeforeStart`; when NO band matches, a system code-default applies
 * (≥24h → 100%, else 0%) — a server-side fallback, never a stored band.
 *
 * **This re-points the v1 `cancellation-policy-bands` and adds the bucket scope.** v1
 * used a bare `bucket` STRING (null = all buckets) and an integer `id`; v2 uses
 * `scope` + `creditBucketId` (a first-class bucket id) and a string `id`.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.12, §3.1 — standard REST):**
 *  - `GET    /v2/admin/cancellation-bands` → {@link CancellationBand}`[]`.
 *  - `POST   /v2/admin/cancellation-bands` ← {@link CancellationBandInput} → the created row.
 *  - `PUT    /v2/admin/cancellation-bands/{id}` ← {@link CancellationBandInput} → the updated row.
 *  - `DELETE /v2/admin/cancellation-bands/{id}`.
 *
 * Reads are parsed DEFENSIVELY (mirrors `services.ts` / `creditBuckets.ts`): a
 * row that cannot place its threshold/refund is dropped; an id-less row is dropped.
 * The list array is extracted tolerantly (bare array or a `bands`/`content` wrapper —
 * the v1 live response wrapped the list as `{ bands: [...] }`).
 */
import { request } from './client';

/** v2 admin cancellation-bands base path (bearer surface). */
const PATH = '/v2/admin/cancellation-bands';

/**
 * A cancellation-band scope (handoff §5.12). DERIVED from whether a bucket is set —
 * `BUCKET` when `creditBucketId` is non-null, else `ALL` (all buckets).
 */
export type CancellationBandScope = 'ALL' | 'BUCKET';

/**
 * One `v2_cancellation_band` row (GET list item / POST+PUT response). `id` is the
 * server row id (absent until persisted); `scope` is the derived `ALL|BUCKET` key;
 * `creditBucketId` is the scoped bucket id, or `null` for "all buckets";
 * `minHoursBeforeStart` is the integer threshold; `refundPercent` is the refunded
 * fraction `0.00–100.00`.
 */
export interface CancellationBand {
  /** Server row id; `null` on a not-yet-persisted band. */
  id: string | null;
  /** The derived scope key (`ALL|BUCKET`). */
  scope: CancellationBandScope;
  /** The scoped credit-bucket id (0052), or `null` for "all buckets". */
  creditBucketId: string | null;
  /** Integer hours before start at/above which this band qualifies. */
  minHoursBeforeStart: number;
  /** Refunded fraction, `0.00–100.00` (two decimals). */
  refundPercent: number;
}

/**
 * POST / PUT body (§5.12). `creditBucketId` `null` = "all buckets" (→ `scope=ALL`),
 * otherwise the picked bucket id (→ `scope=BUCKET`). `scope` is DERIVED from the
 * bucket presence — the admin never picks it. `id` is never in the body — an edit
 * keys on the path segment.
 */
export interface CancellationBandInput {
  /** The scoped bucket id, or `null` for "all buckets". */
  creditBucketId: string | null;
  minHoursBeforeStart: number;
  refundPercent: number;
}

/** Derive the `scope` from whether a bucket is set (the admin picks the bucket; we compute scope). */
export function deriveBandScope(creditBucketId: string | null): CancellationBandScope {
  return creditBucketId === null ? 'ALL' : 'BUCKET';
}

// --- Defensive parsing helpers (mirrors services.ts / creditBuckets.ts) ---------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI keys off — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse one raw band, or null when it cannot place its threshold/refund (a
 * half-identified row can't be rendered or edited — drop it). `creditBucketId` is null
 * when absent/blank ("all buckets"); `scope` is RE-DERIVED from it (we trust the id
 * over a possibly-stale server `scope`). `id` is the string row id, or null when not
 * yet persisted.
 */
function parseBand(raw: unknown): CancellationBand | null {
  if (!isObject(raw)) return null;
  const minHoursBeforeStart = asNumber(raw['minHoursBeforeStart']);
  const refundPercent = asNumber(raw['refundPercent']);
  if (minHoursBeforeStart === null || refundPercent === null) return null;
  // Nested v2 fk id — numeric on the wire (DNKADM-0064); null = "all buckets".
  const creditBucketId = asId(raw['creditBucketId']);
  return {
    id: asId(raw['id']),
    scope: deriveBandScope(creditBucketId),
    creditBucketId,
    minHoursBeforeStart,
    refundPercent,
  };
}

/**
 * Extract the bands array TOLERANTLY from a successful body, returning `[]` when
 * NEITHER a bare array NOR a known wrapper key (`bands`/`cancellationBands`/`content`)
 * yields one (a missing list is a real first-run state, §8).
 */
function extractBands(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['bands'])) return body['bands'];
    if (Array.isArray(body['cancellationBands'])) return body['cancellationBands'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/**
 * Build the POST/PUT JSON body. Sends the derived `scope` + `creditBucketId` (incl.
 * `null` = all buckets) + the threshold/refund.
 */
function toBody(input: CancellationBandInput): Record<string, unknown> {
  return {
    scope: deriveBandScope(input.creditBucketId),
    creditBucketId: input.creditBucketId,
    minHoursBeforeStart: input.minHoursBeforeStart,
    refundPercent: input.refundPercent,
  };
}

function bandPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/cancellation-bands` → the tenant's refund bands, defensively parsed
 * (an unplaceable row dropped). A body that yields no array degrades to `[]` (a real
 * first-run state, §8 — only the code-default applies). Throws a typed `ApiError` on a
 * non-2xx response. The screen sorts by `minHoursBeforeStart` descending and refetches
 * after every write.
 */
export async function listCancellationBands(): Promise<CancellationBand[]> {
  const body = await request<unknown>('GET', PATH);
  return extractBands(body)
    .map(parseBand)
    .filter((b): b is CancellationBand => b !== null);
}

/**
 * `POST /v2/admin/cancellation-bands` — create a band. Sends the derived `scope` +
 * `creditBucketId` (`null` = all buckets) + threshold/refund. Returns the re-parsed
 * created row. Throws a typed `ApiError` (`400`/`409` on the unique
 * `(scope, bucket, minHours)` rule, `403`, upstream). Refetch the list after this
 * resolves.
 */
export async function createCancellationBand(
  input: CancellationBandInput,
): Promise<CancellationBand | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseBand(body);
}

/**
 * `PUT /v2/admin/cancellation-bands/{id}` — edit a band. Same body as create; the row
 * is keyed by the `id` path segment. Returns the re-parsed updated row. Throws a typed
 * `ApiError` (`400`/`404`/`409`, upstream). Refetch the list after this resolves.
 */
export async function updateCancellationBand(
  id: string,
  input: CancellationBandInput,
): Promise<CancellationBand | null> {
  const body = await request<unknown>('PUT', bandPath(id), { json: toBody(input) });
  return parseBand(body);
}

/**
 * `DELETE /v2/admin/cancellation-bands/{id}` — remove a band. Throws a typed
 * `ApiError` (`404` NOT_FOUND when no such row, `403`, upstream forwarded). The
 * screen treats a `404` as already-gone and just refreshes the list; refetch after
 * this resolves.
 */
export async function deleteCancellationBand(id: string): Promise<void> {
  await request<unknown>('DELETE', bandPath(id));
}
